import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ACTIVATION_BENCHMARKS,
  ACTIVATION_MIX,
  ACTIVATION_RETENTION_SPLIT,
  BOUNCE_RATE_BENCHMARKS,
  CHURN_BENCHMARKS,
  CHURN_BY_TENURE,
  CHURN_COMPOUNDING,
  NRR_BENCHMARKS,
  NRR_COMPOSITION,
  NRR_WHALE_TEST,
  STRIPE_MRR_BRIDGE,
  TRAFFIC_SOURCE_SPLIT,
  TRIAL_TO_PAID_BENCHMARKS,
  type BenchmarkRange,
} from "./exampleData";
import {
  ACCENT,
  BORDER,
  CHART_NEGATIVE,
  CHART_POSITIVE,
  CHART_POSITIVE_SEMI_MUTED,
  DESTRUCTIVE,
  MUTED,
  MUTED_FG,
  tooltipStyle,
  xAxisProps,
  yAxisProps,
} from "./chartTheme";
import { formatCompactCurrency, formatCurrency } from "@/lib/calculators";
import ChartCard from "./ChartCard";

interface BenchmarkRangeChartProps {
  title: string;
  caption: string;
  data: BenchmarkRange[];
  /** Formats range bounds for labels and the tooltip (e.g. `${v}%`). */
  format: (value: number) => string;
  domain: [number, number];
  ticks: number[];
  /** Optional dashed threshold line (e.g. the 100% compounding line). */
  reference?: number;
}

/**
 * Floating min–max range bars per segment — how the benchmark guides visualise
 * "what is normal" bands by ACV. Uses recharts' native range-bar form (a
 * [low, high] value), which keeps the explicit domain — a stacked transparent
 * base would silently re-anchor the axis at 0.
 */
const BenchmarkRangeChart = ({
  title,
  caption,
  data,
  format,
  domain,
  ticks,
  reference,
}: BenchmarkRangeChartProps) => {
  const rows = data.map((row) => ({ ...row, range: [row.min, row.max] }));
  return (
    <ChartCard title={title} caption={caption} height={260}>
      <BarChart data={rows} margin={{ top: 16, right: 8, left: 8, bottom: 0 }} barCategoryGap="30%">
        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
        <XAxis dataKey="label" {...xAxisProps} interval={0} fontSize={11} />
        <YAxis
          tickFormatter={(v) => format(Number(v))}
          domain={domain}
          ticks={ticks}
          width={48}
          {...yAxisProps}
        />
        <Tooltip
          cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
          contentStyle={tooltipStyle}
          formatter={(_value, _name, item) => {
            const row = item?.payload as BenchmarkRange | undefined;
            return row ? [`${format(row.min)} – ${format(row.max)}`, "Typical range"] : ["", ""];
          }}
        />
        {reference !== undefined && (
          <ReferenceLine y={reference} stroke={MUTED_FG} strokeDasharray="4 4" />
        )}
        <Bar dataKey="range" fill={CHART_POSITIVE} radius={[4, 4, 4, 4]} isAnimationActive={false}>
          <LabelList
            dataKey="max"
            position="top"
            formatter={(v: number) => format(Number(v))}
            fontSize={11}
            fill={MUTED_FG}
          />
        </Bar>
      </BarChart>
    </ChartCard>
  );
};

export const BounceRateBenchmarksChart = () => (
  <BenchmarkRangeChart
    title="Bounce rate benchmarks by site type"
    caption="Typical bounce-rate bands (single-page definition): ecommerce runs 20–45%, multi-page SaaS sites 26–55%, while single-CTA landing pages and content sites live at 60–90% by design — the same number can be healthy on one site type and a flag on another."
    data={BOUNCE_RATE_BENCHMARKS}
    format={(v) => `${v}%`}
    domain={[0, 100]}
    ticks={[0, 25, 50, 75, 100]}
  />
);

const formatPct = (v: number) => `${v}%`;

/**
 * Grouped bars per traffic source — share of visits (neutral) vs. share of
 * revenue (green) — so the divergence is visible: the channel that sends the
 * most visits isn't the channel that brings the most revenue.
 */
export const TrafficSourcesChart = () => (
  <ChartCard
    title="Traffic by source: visits vs. revenue"
    caption="Share of visits and share of revenue by channel (illustrative). Organic and direct send the most visits, but paid brings the most revenue — ranking channels by traffic hides which ones actually pay off. This is why source reports are only useful once revenue is attached."
    height={260}
  >
    <BarChart
      data={TRAFFIC_SOURCE_SPLIT}
      margin={{ top: 16, right: 8, left: 8, bottom: 0 }}
      barCategoryGap="28%"
    >
      <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
      <XAxis dataKey="source" {...xAxisProps} interval={0} fontSize={11} />
      <YAxis
        tickFormatter={formatPct}
        domain={[0, 40]}
        ticks={[0, 10, 20, 30, 40]}
        {...yAxisProps}
      />
      <Tooltip
        cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
        contentStyle={tooltipStyle}
        formatter={(value: number, name: string) => [formatPct(value), name]}
      />
      <Legend wrapperStyle={{ fontSize: 12 }} />
      <Bar
        dataKey="visits"
        name="Visits"
        fill={MUTED}
        radius={[4, 4, 0, 0]}
        isAnimationActive={false}
      >
        <LabelList
          dataKey="visits"
          position="top"
          formatter={formatPct}
          fontSize={11}
          fill={MUTED_FG}
        />
      </Bar>
      <Bar
        dataKey="revenue"
        name="Revenue"
        fill={CHART_POSITIVE}
        radius={[4, 4, 0, 0]}
        isAnimationActive={false}
      >
        <LabelList
          dataKey="revenue"
          position="top"
          formatter={formatPct}
          fontSize={11}
          fill={MUTED_FG}
        />
      </Bar>
    </BarChart>
  </ChartCard>
);

export const NrrBenchmarksChart = () => (
  <BenchmarkRangeChart
    title="NRR benchmarks by ACV segment"
    caption="Typical NRR bands: SMB products live at 90–105%, mid-market at 100–115%, enterprise at 110–130%. The dashed line is 100% — above it, the existing base compounds on its own."
    data={NRR_BENCHMARKS}
    format={(v) => `${v}%`}
    domain={[90, 135]}
    ticks={[90, 100, 110, 120, 130]}
    reference={100}
  />
);

const pct = (v: number) => `${v}%`;

/**
 * Same 110% NRR, decomposed — a durable 95% GRR plus modest expansion vs an
 * 80% GRR propped up by heavy expansion. Stacked bars anchored at 0 so each
 * company's tower shows how much of it is retained base vs upsell.
 */
export const NrrGrrCompositionChart = () => (
  <ChartCard
    title="Same NRR, different businesses"
    caption="Both companies report 110% NRR. One keeps 95% of its base and adds 15 points of expansion; the other leaks 20% of its base every year and papers over it with 30 points of upsell — a number that survives only as long as the expansion engine does."
    height={260}
  >
    <BarChart
      data={NRR_COMPOSITION}
      margin={{ top: 16, right: 8, left: 8, bottom: 0 }}
      barCategoryGap="35%"
    >
      <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
      <XAxis dataKey="label" {...xAxisProps} interval={0} fontSize={11} />
      <YAxis
        tickFormatter={pct}
        domain={[0, 120]}
        ticks={[0, 25, 50, 75, 100]}
        width={48}
        {...yAxisProps}
      />
      <Tooltip
        cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
        contentStyle={tooltipStyle}
        formatter={(value: number, name: string) => [
          name === "grr" ? `${value}% GRR` : `+${value}pp expansion`,
          "NRR 110%",
        ]}
      />
      <ReferenceLine y={100} stroke={MUTED_FG} strokeDasharray="4 4" />
      <Bar dataKey="grr" stackId="nrr" fill={CHART_POSITIVE} isAnimationActive={false}>
        <LabelList
          dataKey="grr"
          position="insideTop"
          formatter={(v: number) => `GRR ${v}%`}
          fontSize={11}
          fill="#ffffff"
        />
      </Bar>
      <Bar
        dataKey="expansion"
        stackId="nrr"
        fill={CHART_POSITIVE_SEMI_MUTED}
        radius={[4, 4, 0, 0]}
        isAnimationActive={false}
      >
        <LabelList
          dataKey="expansion"
          position="top"
          formatter={(v: number) => `+${v}pp expansion`}
          fontSize={11}
          fill={MUTED_FG}
        />
      </Bar>
    </BarChart>
  </ChartCard>
);

/**
 * The whale test — reported NRR vs the same cohort with the five biggest
 * expansion accounts removed. Falling below 100% reveals concentration risk.
 */
export const NrrWhaleTestChart = () => (
  <ChartCard
    title="The whale test"
    caption="Reported NRR is 115% — but strip out the five biggest expansion accounts and the rest of the base retains only 96%. Below the 100% line, the business is shrinking for every customer that isn't a whale."
    height={240}
  >
    <BarChart
      data={NRR_WHALE_TEST}
      margin={{ top: 16, right: 8, left: 8, bottom: 0 }}
      barCategoryGap="35%"
    >
      <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
      <XAxis dataKey="label" {...xAxisProps} interval={0} fontSize={11} />
      <YAxis
        tickFormatter={pct}
        domain={[90, 120]}
        ticks={[90, 100, 110, 120]}
        width={48}
        {...yAxisProps}
      />
      <Tooltip
        cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
        contentStyle={tooltipStyle}
        formatter={(value: number) => [pct(value), "NRR"]}
      />
      <ReferenceLine y={100} stroke={MUTED_FG} strokeDasharray="4 4" />
      <Bar dataKey="value" radius={[4, 4, 0, 0]} isAnimationActive={false}>
        <LabelList
          dataKey="value"
          position="top"
          formatter={(v: number) => pct(Number(v))}
          fontSize={11}
          fill={MUTED_FG}
        />
        {NRR_WHALE_TEST.map((row) => (
          <Cell key={row.label} fill={row.value >= 100 ? CHART_POSITIVE : CHART_NEGATIVE} />
        ))}
      </Bar>
    </BarChart>
  </ChartCard>
);

/**
 * Simple single-series ChartCard bars for the guide and glossary articles —
 * plain category bars (no truncated domain, no ranges), labels on top.
 */
export const GuideBarChart = ({
  title,
  caption,
  data,
  color,
  seriesName = "Value",
  format = pct,
  yWidth = 48,
  height = 240,
}: {
  title: string;
  caption: string;
  data: Array<{ label: string; value: number }>;
  color: string;
  seriesName?: string;
  format?: (value: number) => string;
  yWidth?: number;
  height?: number;
}) => (
  <ChartCard title={title} caption={caption} height={height}>
    <BarChart data={data} margin={{ top: 16, right: 8, left: 8, bottom: 0 }} barCategoryGap="30%">
      <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
      <XAxis dataKey="label" {...xAxisProps} interval={0} fontSize={11} />
      <YAxis tickFormatter={(v) => format(Number(v))} width={yWidth} {...yAxisProps} />
      <Tooltip
        cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
        contentStyle={tooltipStyle}
        formatter={(value: number) => [format(value), seriesName]}
      />
      <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} isAnimationActive={false}>
        <LabelList
          dataKey="value"
          position="top"
          formatter={(v: number) => format(Number(v))}
          fontSize={11}
          fill={MUTED_FG}
        />
      </Bar>
    </BarChart>
  </ChartCard>
);

export const ChurnCompoundingChart = () => (
  <GuideBarChart
    title="What monthly churn compounds to in a year"
    caption="Each bar is a monthly churn rate compounded over twelve months: 1 − (1 − monthly)¹². A 5%-a-month churn rate that sounds survivable removes 46% of the customer base in a year; 7% removes most of it."
    data={CHURN_COMPOUNDING}
    color={CHART_NEGATIVE}
    seriesName="Annual churn"
  />
);

export const ChurnByTenureChart = () => (
  <GuideBarChart
    title="Monthly churn by customer tenure"
    caption="Churn is front-loaded: customers in their first quarter churn at 8% a month, falling to about 1% by year three. A fast-growing company's base skews young, so its blended churn rate always reads worse than its steady state."
    data={CHURN_BY_TENURE}
    color={CHART_NEGATIVE}
    seriesName="Monthly churn"
  />
);

const bridgeColor = (kind: "total" | "gain" | "loss"): string => {
  if (kind === "gain") return ACCENT;
  if (kind === "loss") return DESTRUCTIVE;
  return MUTED;
};

/**
 * Bridge from Stripe's reported Billing MRR to real MRR — floating waterfall
 * bars in the MrrMovementChart style: what Stripe's definition leaves out
 * (usage-based revenue, other processors) and what it over-counts (past-due,
 * unconfigured discounts).
 */
export const StripeMrrBridgeChart = () => (
  <ChartCard
    title="From Stripe's MRR to your real MRR"
    caption="Stripe Billing reports $23,200 — but it counts no usage-based revenue (+$1,500) and sees no other processors (+$1,000), while carrying past-due subscriptions that may never pay (−$400) and coupons the config never subtracts (−$300). Real MRR: $25,000."
    height={260}
  >
    <BarChart data={STRIPE_MRR_BRIDGE} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
      <XAxis dataKey="label" {...xAxisProps} interval={0} fontSize={11} />
      <YAxis tickFormatter={(v) => formatCompactCurrency(Number(v))} width={52} {...yAxisProps} />
      <Tooltip
        cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
        contentStyle={tooltipStyle}
        formatter={(_value, _name, item) => {
          const row = item?.payload as { value: number; total: number; kind: string } | undefined;
          if (!row) return ["", ""];
          const sign = row.kind === "loss" ? "−" : row.kind === "gain" ? "+" : "";
          return [
            `${sign}${formatCurrency(row.value)}`,
            `Running total ${formatCurrency(row.total)}`,
          ];
        }}
      />
      {/* Invisible base lifts each floating bar to its starting height. */}
      <Bar dataKey="base" stackId="bridge" fill="transparent" isAnimationActive={false} />
      <Bar dataKey="value" stackId="bridge" radius={[4, 4, 0, 0]} isAnimationActive={false}>
        {STRIPE_MRR_BRIDGE.map((bar) => (
          <Cell key={bar.label} fill={bridgeColor(bar.kind)} />
        ))}
      </Bar>
    </BarChart>
  </ChartCard>
);

export const TrialToPaidBenchmarksChart = () => (
  <BenchmarkRangeChart
    title="Trial-to-paid conversion by signup motion"
    caption="Typical trial-to-paid bands: freemium converts 2–5% of users, opt-in free trials without a card 8–20%, and card-up-front trials 30–50% — the card filters intent before the trial even starts."
    data={TRIAL_TO_PAID_BENCHMARKS}
    format={(v) => `${v}%`}
    domain={[0, 55]}
    ticks={[0, 10, 20, 30, 40, 50]}
  />
);

export const ActivationBenchmarksChart = () => (
  <BenchmarkRangeChart
    title="Activation rate by product complexity"
    caption="Typical activation bands: simple single-player tools activate 40–60% of signups, multi-step products 25–40%, and complex or enterprise setups 15–30% — the more your aha moment depends on data, teammates or integrations, the lower the raw rate."
    data={ACTIVATION_BENCHMARKS}
    format={(v) => `${v}%`}
    domain={[0, 65]}
    ticks={[0, 15, 30, 45, 60]}
  />
);

export const ActivationMixChart = () => (
  <GuideBarChart
    title="The mix illusion"
    caption="Both segments activate at exactly the same rate in both quarters — self-serve at 25%, sales-assisted at 55%. Shifting the signup mix from 80/20 to 60/40 lifts the blended rate from 31% to 37% while nothing about the product changed."
    data={ACTIVATION_MIX}
    color={CHART_POSITIVE}
    seriesName="Activation rate"
  />
);

/**
 * Retention curves split by activation — the guide's "retention test", and the
 * first line chart in the set: curves are the natural form for retention decay.
 */
export const ActivationRetentionSplitChart = () => (
  <ChartCard
    title="The retention test"
    caption="Accounts that hit the activation event retain 62% at week eight; those that never activated collapse to 15%. If your two curves look similar, the activation event is theatre — it measures onboarding compliance, not received value."
    height={260}
  >
    <LineChart data={ACTIVATION_RETENTION_SPLIT} margin={{ top: 16, right: 8, left: 8, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
      <XAxis dataKey="label" {...xAxisProps} fontSize={11} />
      <YAxis
        tickFormatter={pct}
        domain={[0, 100]}
        ticks={[0, 25, 50, 75, 100]}
        width={48}
        {...yAxisProps}
      />
      <Tooltip
        cursor={{ stroke: MUTED_FG, strokeDasharray: "3 3" }}
        contentStyle={tooltipStyle}
        formatter={(value: number, name: string) => [
          pct(value),
          name === "activated" ? "Activated" : "Never activated",
        ]}
      />
      <Line
        type="monotone"
        dataKey="activated"
        stroke={CHART_POSITIVE}
        strokeWidth={2}
        dot={false}
        isAnimationActive={false}
      />
      <Line
        type="monotone"
        dataKey="notActivated"
        stroke={CHART_NEGATIVE}
        strokeWidth={2}
        dot={false}
        isAnimationActive={false}
      />
    </LineChart>
  </ChartCard>
);

export const ChurnBenchmarksChart = () => (
  <BenchmarkRangeChart
    title="Monthly logo churn by ACV segment"
    caption="Typical monthly customer-churn bands: 3–7% for SMB, 1–2.5% for mid-market, under 1% for enterprise. The bigger the contract, the closer to zero churn must be."
    data={CHURN_BENCHMARKS}
    format={(v) => `${v}%`}
    domain={[0, 8]}
    ticks={[0, 2, 4, 6, 8]}
  />
);
